import { parseStartingCostToNumber } from '@client/components/pricing/utils/pricing-estimation';
import {
	estimateDisplayAtom,
	estimateServiceBreakdownAtom,
	pricingSnackbarAtom,
	startingCostAtom,
} from '@client/jotai/storefront/pricing';
import { usePersistedEstimate } from '@hooks/use-persisted-estimate';
import { getAnonymousId } from '@redux/settings';
import { inboxUrl } from '@settings';
import { useAtomValue, useSetAtom } from 'jotai';
import type { Dispatch, SetStateAction } from 'react';
import { useCallback, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useSendPricingInquiry } from '../../../../../../../lib/storefront-pricing/hooks/useSendPricingInquiry';
import type {
	ContactFormData,
	PricingEstimationService,
	PricingEstimationTotal,
} from '../../../../types';
import type { WeddingDateData } from '../../../utils/weddingDate';
import {
	formatWeddingDateForApi,
	formatWeddingDateForMessage,
} from '../utils/formatWeddingDateForMessage';
import { persistEstimateData } from '../utils/persistEstimateData';

interface UseSubmitContactParams {
	weddingDate: WeddingDateData;
	guestCount: string;
	selectedServices: PricingEstimationService[];
	unavailableServices: PricingEstimationService[];
	estimate: PricingEstimationTotal | null;
	storefrontId: string;
	vendorId: string | undefined;
	profile: {
		profileEmail: string;
		profileFirstName: string;
		profileLastName: string;
		profileGuestCount: string;
	};
	contactData: ContactFormData;
	setFormView: Dispatch<SetStateAction<'form' | 'loading' | 'error'>>;
	setCurrentStep: Dispatch<SetStateAction<number>>;
	setSelectedServices: Dispatch<SetStateAction<PricingEstimationService[]>>;
	setGuestCount: Dispatch<SetStateAction<string>>;
	setWeddingDate: Dispatch<SetStateAction<WeddingDateData>>;
	setContactData: Dispatch<SetStateAction<ContactFormData>>;
	closeCallback: () => void;
	onNewUserCreated?: () => void;
}

export const useSubmitContact = ({
	weddingDate,
	guestCount,
	selectedServices,
	unavailableServices,
	estimate,
	storefrontId,
	vendorId,
	profile,
	contactData,
	setFormView,
	setCurrentStep,
	setSelectedServices,
	setGuestCount,
	setWeddingDate,
	setContactData,
	closeCallback,
	onNewUserCreated,
}: UseSubmitContactParams) => {
	const vendor = useSelector((state: Redux.State) => state.vendor.vendor);
	const anonymousId = useSelector(getAnonymousId);
	const { sendInquiry } = useSendPricingInquiry();
	const { setEstimate: persistEstimate } = usePersistedEstimate(storefrontId);
	const setEstimateDisplay = useSetAtom(estimateDisplayAtom);
	const setEstimateServiceBreakdown = useSetAtom(estimateServiceBreakdownAtom);
	const setSnackbarMessage = useSetAtom(pricingSnackbarAtom);
	const startingCostRaw = useAtomValue(startingCostAtom);
	const submittingGuard = useRef(false);
	const [isSubmitting, setIsSubmitting] = useState(false);

	const handleSubmitContact = useCallback(async () => {
		if (submittingGuard.current) return;
		if (!estimate) {
			setFormView('error');
			return;
		}
		submittingGuard.current = true;
		setFormView('loading');
		setIsSubmitting(true);

		const apiWeddingDate = formatWeddingDateForApi(weddingDate);
		const profileWeddingDate =
			weddingDate.dateOption === 'specificDate' ? apiWeddingDate : undefined;
		const guestCountNum = Number(guestCount) || 0;

		const memberInfo = {
			email: contactData.email,
			firstName: contactData.firstName,
			lastName: contactData.lastName,
		};

		let isNewUser = false;
		let conversationUrl: string | undefined;
		try {
			if (vendorId && storefrontId) {
				const result = await sendInquiry({
					weddingDate: apiWeddingDate,
					weddingDateDisplay:
						formatWeddingDateForMessage(weddingDate) ?? undefined,
					weddingDateForProfile: profileWeddingDate,
					guestCount: guestCountNum,
					selectedServices,
					unavailableServices,
					estimate,
					storefrontId,
					vendorId,
					memberInfo,
					vendor,
					anonymousId: anonymousId || undefined,
					startingPrice: parseStartingCostToNumber(startingCostRaw),
				});

				if (result.status === 'FAILED') {
					setFormView('error');
					return;
				}
				isNewUser = result.isExistingUser === false;
				conversationUrl = result.conversationId
					? `${inboxUrl}/${result.conversationId}`
					: undefined;
			}

			persistEstimateData({
				estimate,
				weddingDate,
				guestCount: guestCountNum,
				rawGuestCount: guestCount,
				selectedServices,
				unavailableServices,
				storefrontId,
				contactData,
				setEstimateDisplay,
				setEstimateServiceBreakdown,
				persistEstimate,
			});

			setCurrentStep(1);
			setSelectedServices([]);
			setGuestCount(profile.profileGuestCount);
			setWeddingDate({
				dateOption: '',
				specificDate: '',
				month: '',
				year: '',
			});
			setContactData({
				firstName: profile.profileFirstName,
				lastName: profile.profileLastName,
				email: profile.profileEmail,
			});
			submittingGuard.current = false;
			setIsSubmitting(false);
			if (vendorId && storefrontId) {
				setSnackbarMessage({
					message: 'Your details were shared with this venue.',
					conversationUrl,
				});
			}
			closeCallback();
			if (isNewUser) {
				onNewUserCreated?.();
			}
		} catch {
			setFormView('error');
		} finally {
			submittingGuard.current = false;
			setIsSubmitting(false);
		}
	}, [
		weddingDate,
		guestCount,
		profile,
		contactData,
		vendorId,
		storefrontId,
		selectedServices,
		unavailableServices,
		estimate,
		sendInquiry,
		vendor,
		anonymousId,
		startingCostRaw,
		closeCallback,
		setFormView,
		setEstimateDisplay,
		setEstimateServiceBreakdown,
		persistEstimate,
		setSnackbarMessage,
		setCurrentStep,
		setSelectedServices,
		setGuestCount,
		setWeddingDate,
		setContactData,
		onNewUserCreated,
	]);

	return { handleSubmitContact, isSubmitting };
};
